import type { ProjectRaw as Project } from "db/custom/schema/projects";

const CMS_BASE_URL = process.env.NEXT_PUBLIC_CMS_API_URL || 'http://localhost:4321';

export interface ProjectsResponse {
    data: Project[];
    total: number;
}

export async function fetchProjects(): Promise<ProjectsResponse> {
    try {
        const url = `${CMS_BASE_URL}/api/v1/projects?limit=100&order=order`;

        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
            },
            // Enable caching for SSR
            next: {
                revalidate: 3600, // Revalidate every hour (as requested)
            },
        });

        if (!response.ok) throw new Error(`Failed to fetch projects: ${response.status} ${response.statusText}`);

        const data = await response.json() as ProjectsResponse;

        return {
            data: data.data || [],
            total: data.total || 0,
        };
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
}

export async function fetchProject(id: string): Promise<Project> {
    try {
        const url = `${CMS_BASE_URL}/api/v1/projects?id=${id}`;

        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
            },
            next: {
                revalidate: 3600, // Revalidate every hour
            },
        });

        if (!response.ok) throw new Error(`Failed to fetch project: ${response.status} ${response.statusText}`);

        const data = await response.json() as ProjectsResponse;

        if (!data.data || data.data.length === 0) throw new Error(`Project with id ${id} not found`);

        return data.data[0]!;
    } catch (error) {
        console.error('Error fetching project:', error);
        throw error;
    }
}

// Query key factory for consistent key management
export const projectsQueryKeys = {
    all: ['projects'] as const,
    lists: () => [...projectsQueryKeys.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...projectsQueryKeys.lists(), { filters }] as const,
    details: () => [...projectsQueryKeys.all, 'detail'] as const,
    detail: (id: string) => [...projectsQueryKeys.details(), id] as const,
    featured: () => [...projectsQueryKeys.all, 'featured'] as const,
};

export function getProjectById(projects: Project[], id: string): Project | undefined {
    return projects.find(project => project.id === id);
}
